import React from 'react';

import ProteinActions from 'molbio/aa-sequences/AaSequenceActions';
import ProteinConstants from 'molbio/aa-sequences/util/AaSequenceConstants';
import * as ProteinProperties from 'molbio/aa-sequences/util/AaSequenceProperties';
import {INPUT_BUBBLE_OPERATION, InputBubble} from 'molbio/maps/InputBubble';
import {Selection} from 'molbio/maps/selectables/Selection';
import {selectRange} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';

interface Props {
  proteinId: string;
  selection: Selection;
  top: number;
  left: number;
  offset: number;
  isPositionedAbove: boolean;
  onRequestClose: () => void;
  pasteText: string;
  operation: INPUT_BUBBLE_OPERATION;
}

function ProteinInputBubble(props: Props): JSX.Element {
  function onCommitAminoAcidsSuccess(start: number, end: number | null): void {
    selectRange(props.proteinId, start, end);
    props.onRequestClose();
  }

  function filterValidAminoAcids(aminoAcids: string): string {
    return aminoAcids
      .split('')
      .filter((aa) => ProteinProperties.areAminoAcidsValid(aa))
      .join('');
  }

  const onReplaceResidues = (residues: string, start: number, end: number): void => {
    if (residues.length !== 0) {
      ProteinActions.replaceAminoAcids(props.proteinId, {
        start,
        end,
        aminoAcids: residues,
        onSuccess: () => {
          onCommitAminoAcidsSuccess(start, start + residues.length);
        },
      });
    } else {
      ProteinActions.deleteAminoAcids(
        props.proteinId,
        {
          start,
          end,
        },
        () => {
          onCommitAminoAcidsSuccess(start, null);
        }
      );
    }
  };

  const onInsertResidues = (residues: string, position: number): void => {
    ProteinActions.insertAminoAcids(props.proteinId, {
      position,
      aminoAcids: residues,
      onSuccess: () => {
        onCommitAminoAcidsSuccess(position, position + residues.length);
      },
    });
  };
  return (
    <InputBubble
      selection={props.selection}
      pastedBases={props.pasteText}
      featureCounts={null}
      operation={props.operation}
      onReplaceOrDeleteResidues={onReplaceResidues}
      onInsertResidues={onInsertResidues}
      top={props.top}
      left={props.left}
      offset={props.offset}
      isPositionedAbove={props.isPositionedAbove}
      sanitizeResidues={filterValidAminoAcids}
      onRequestClose={props.onRequestClose}
      residueName="residue"
      sequenceStartIndex={1} // AA sequences cannot have a non-one start index
      onCheckNextResidueValid={(nextResidue: string) =>
        ProteinConstants.ALL_AMINO_ACIDS.includes(nextResidue.toUpperCase())
      }
    />
  );
}

export default ProteinInputBubble;
